import type { GetSitrepsResponse } from 'components/Reports/reports.types';

import type {
  PresentationSlide,
  PresentationSlideKind,
} from './presentationHooks';

const SLIDE_KINDS: PresentationSlideKind[] = [
  'form',
  'sitstat',
  'cover',
  'report',
  'briefing',
  'blank',
];

export function newSlide(
  kind: PresentationSlide['kind'],
  sourceId: string
): PresentationSlide {
  return {
    id: `slide-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    kind,
    sourceId,
    notes: '',
    ...(kind === 'report' ? { layout: 'document' as const } : {}),
  };
}

export function encodeSource(
  slide: Pick<PresentationSlide, 'kind' | 'sourceId'>
): string {
  return `${slide.kind}|${slide.sourceId}`;
}

export function decodeSource(
  value: string
): Pick<PresentationSlide, 'kind' | 'sourceId'> | null {
  const split = value.indexOf('|');
  if (split <= 0) return null;
  const kind = value.slice(0, split);
  const sourceId = value.slice(split + 1);
  if (!SLIDE_KINDS.includes(kind as PresentationSlideKind) || !sourceId) {
    return null;
  }
  return { kind: kind as PresentationSlideKind, sourceId };
}

/** Keep notes, and only the fields that belong to the newly chosen kind. */
export function applySlideSource(
  slide: PresentationSlide,
  next: Pick<PresentationSlide, 'kind' | 'sourceId'>
): PresentationSlide {
  const base: PresentationSlide = {
    id: slide.id,
    notes: slide.notes,
    kind: next.kind,
    sourceId: next.sourceId,
  };
  if (next.kind === 'report') {
    base.layout =
      slide.layout === 'quad' || slide.layout === 'widgets'
        ? slide.layout
        : 'document';
    if (slide.layout === 'widgets') {
      base.widgetCount = slide.widgetCount;
      base.widgetOffset = slide.widgetOffset;
    }
  }
  if (next.kind === 'cover') {
    base.eyebrow = slide.eyebrow;
    base.title = slide.title;
    base.dateLine = slide.dateLine;
    base.timeLine = slide.timeLine;
    base.mark = slide.mark;
  }
  if (next.kind === 'sitstat') {
    base.viewLayout =
      slide.viewLayout === 'quad' || slide.viewLayout === 'framed'
        ? slide.viewLayout
        : 'current';
  }
  if (next.kind === 'briefing') {
    base.title = slide.title;
    base.subtitle = slide.subtitle;
    base.bullets = slide.bullets;
    base.quadrants = slide.quadrants;
    base.showMap = slide.showMap;
  }
  if (next.kind === 'blank') {
    base.body = slide.body;
  }
  return base;
}

export function isAorReport(sitrep: GetSitrepsResponse): boolean {
  return Boolean(sitrep.aor_id) && !sitrep.incident_id;
}

/** Hub decks can add a slide for a published area report or incident report. */
export function isPresentableReport(sitrep: GetSitrepsResponse): boolean {
  return Boolean(sitrep.incident_id || sitrep.aor_id);
}

export function reportSlideLabel(sitrep: GetSitrepsResponse): string {
  const area = sitrep.spatial_filter_label?.trim();
  return area || sitrep.name || 'Current report';
}

export function htmlToBullets(html: string | null | undefined): string[] {
  if (!html) return [];
  const text = html
    .replace(/<li[^>]*>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|tr)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"');
  return text
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .slice(0, 8);
}
